import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiShoppingCart, HiDocumentText, HiCurrencyRupee, HiCheckCircle } from 'react-icons/hi2';
import { getOrders, getInvoices } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';

const orderStatusBadge = s => {
  const map = { pending: 'badge-yellow', confirmed: 'badge-blue', dispatched: 'badge-purple', delivered: 'badge-green', cancelled: 'badge-red' };
  return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
};

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getOrders({ limit: 5 }),
      getInvoices({ limit: 5 }),
    ])
      .then(([o, i]) => {
        setOrders(o.data.data || []);
        setInvoices(i.data.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totalDue = invoices.filter(i => ['unpaid', 'partial', 'overdue'].includes(i.status))
    .reduce((s, i) => s + (parseFloat(i.amount) - parseFloat(i.paid_amount)), 0);

  return (
    <div className="content-area">
      <div className="page-header">
        <div>
          <h1 className="page-title">Welcome, {user?.name}! 👋</h1>
          <p className="text-muted">Your water delivery dashboard</p>
        </div>
        <a href="tel:8374422411" className="btn btn-primary">📞 Call Us to Order</a>
      </div>

      <div className="grid grid-3" style={{ marginBottom: '1.5rem' }}>
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ background: '#dbeafe', padding: '0.75rem', borderRadius: '0.5rem' }}>
              <HiShoppingCart style={{ color: '#1d4ed8', fontSize: '1.5rem' }} />
            </div>
            <div>
              <p className="stat-label">Total Orders</p>
              <p className="stat-value">{orders.length}</p>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ background: '#fef3c7', padding: '0.75rem', borderRadius: '0.5rem' }}>
              <HiCurrencyRupee style={{ color: '#b45309', fontSize: '1.5rem' }} />
            </div>
            <div>
              <p className="stat-label">Amount Due</p>
              <p className="stat-value" style={{ color: totalDue > 0 ? '#dc2626' : '#16a34a' }}>₹{totalDue.toFixed(0)}</p>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ background: '#dcfce7', padding: '0.75rem', borderRadius: '0.5rem' }}>
              <HiCheckCircle style={{ color: '#16a34a', fontSize: '1.5rem' }} />
            </div>
            <div>
              <p className="stat-label">Deliveries Done</p>
              <p className="stat-value">{orders.filter(o => o.status === 'delivered').length}</p>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className="grid grid-2">
          {/* Recent Orders */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title"><HiShoppingCart style={{ marginRight: '0.4rem' }} />Recent Orders</h2>
              <Link to="/portal/orders" className="btn btn-secondary btn-sm">View All</Link>
            </div>
            {orders.length === 0 ? (
              <div className="card-body" style={{ textAlign: 'center', color: '#94a3b8' }}>No orders yet</div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>Order #</th><th>Amount</th><th>Status</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {orders.map(o => (
                      <tr key={o.id}>
                        <td><span className="badge badge-blue">{o.order_number}</span></td>
                        <td style={{ fontWeight: 600 }}>₹{parseFloat(o.total_amount).toFixed(0)}</td>
                        <td>{orderStatusBadge(o.status)}</td>
                        <td className="text-muted">{format(new Date(o.order_date), 'dd MMM')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent Bills */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title"><HiDocumentText style={{ marginRight: '0.4rem' }} />Recent Bills</h2>
              <Link to="/portal/bills" className="btn btn-secondary btn-sm">View All</Link>
            </div>
            {invoices.length === 0 ? (
              <div className="card-body" style={{ textAlign: 'center', color: '#94a3b8' }}>No bills yet</div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>Invoice #</th><th>Amount</th><th>Paid</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {invoices.map(i => (
                      <tr key={i.id}>
                        <td style={{ fontSize: '0.8rem' }}>{i.invoice_number}</td>
                        <td>₹{parseFloat(i.amount).toFixed(0)}</td>
                        <td style={{ color: '#16a34a', fontWeight: 600 }}>₹{parseFloat(i.paid_amount).toFixed(0)}</td>
                        <td>
                          <span className={`badge ${i.status === 'paid' ? 'badge-green' : i.status === 'partial' ? 'badge-yellow' : 'badge-red'}`}>
                            {i.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contact card */}
      <div className="card" style={{ marginTop: '1.5rem', background: 'linear-gradient(135deg, #0c4a6e, #0ea5e9)', color: 'white', border: 'none' }}>
        <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h3 style={{ fontWeight: 700, fontSize: '1.125rem' }}>Need to place an order?</h3>
            <p style={{ opacity: 0.85, fontSize: '0.875rem' }}>Call us or WhatsApp — we deliver same day!</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <a href="tel:8374422411" className="btn btn-lg" style={{ background: 'white', color: '#0ea5e9', fontWeight: 600 }}>📞 8374422411</a>
            <a href="https://wa.me/918374422411" target="_blank" rel="noreferrer" className="btn btn-lg" style={{ background: '#25d366', color: 'white' }}>WhatsApp</a>
          </div>
        </div>
      </div>
    </div>
  );
}
